'use client';

import {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@lulalife/design-system';
import type { ReactNode } from 'react';
import { SETTINGS_PAGE_INSET } from './SettingsPage';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * `page` is the design's page title (20px); `section` is its section title
 * (16px). The two are the only heading sizes the settings design uses.
 *
 * MIND THE TOKEN-NAME DRIFT. The Figma library calls the 20px page title
 * `Desktop/Heading/md`, but the published design system's `typography-heading-md`
 * is 24px — its 20px step is `heading-sm`. The names are one step apart, so
 * reading the Figma name and typing the DS name of the same word gives a title
 * one size too large, which is exactly how this shipped at 24px. Verified in
 * tokens.css: `heading-md` -> `--text-2xl` (24px/32), `heading-sm` -> `--text-xl`
 * (20px/28, matching Figma's 20/500/28 exactly). Logged in
 * docs/design-system-gaps.md.
 */
type HeaderSize = 'page' | 'section';

type SettingsPageHeaderProps = {
  /** Heading text. */
  title: ReactNode;
  /**
   * Which heading element to render. Defaults to `h1` per the mockup's page
   * header. Pass the level that fits the document outline of the page the
   * header sits on — the element and the visual size are independent (`size`).
   */
  as?: HeadingLevel;
  /** Visual treatment: the prototype's page title, or a section title. */
  size?: HeaderSize;
  /** Supporting copy under the title. */
  description?: ReactNode;
  /** Rendered inline after the title — the mockup's `.scopechip` slot. */
  pill?: ReactNode;
  /** Right-aligned controls, e.g. a primary action Button. */
  actions?: ReactNode;
  /** Forwarded to the heading element so existing `id`-based labelling survives. */
  titleId?: string;
  /**
   * Adds the settings page inset. Only for a header rendered OUTSIDE a
   * `SettingsPage` — which already carries it, so passing this inside one would
   * indent the title 8px past the rows beneath it. Markets is the one caller:
   * its header sits in the shared listing shell's `aboveContent` slot.
   */
  inset?: boolean;
  className?: string;
};

const TITLE_TYPOGRAPHY: Record<HeaderSize, string> = {
  page: 'typography-heading-sm',
  section: 'typography-heading-xs',
};

/**
 * Title / description / actions block that opens a settings page or section.
 *
 * Layout only — every visual value comes from a DS token utility, so this holds
 * no colours or sizes of its own.
 */
export const SettingsPageHeader = ({
  title,
  as: Heading = 'h1',
  size = 'page',
  description,
  pill,
  actions,
  titleId,
  inset = false,
  className,
}: SettingsPageHeaderProps) => {
  // A page title at its default h1 is the DS `PageHeader` as Figma draws it (title and
  // description with no gap, the DS spacing to what follows): parity #276. A section title or
  // another heading level keeps the local layout, which `PageHeader` cannot draw.
  if (size === 'page' && Heading === 'h1') {
    return (
      <div
        data-lula-ds=""
        className={`font-sans${inset ? ` ${SETTINGS_PAGE_INSET}` : ''}${
          className ? ` ${className}` : ''
        }`}
      >
        <PageHeader className="bg-transparent">
          {/* Figma's title sits 4px lower and touches its description: the package's own 4px top
              padding and no gap (0.33.1), so the header stays 52. */}
          <PageHeaderContent>
            <PageHeaderTitle id={titleId} trailingSlot={pill}>
              {title}
            </PageHeaderTitle>
            {description && (
              <PageHeaderDescription className="max-w-size-container-lg">
                {description}
              </PageHeaderDescription>
            )}
          </PageHeaderContent>
          {actions && <PageHeaderActions>{actions}</PageHeaderActions>}
        </PageHeader>
      </div>
    );
  }

  return (
    <div
      data-lula-ds=""
      className={`flex flex-wrap items-start justify-between gap-space-16 font-sans${
        inset ? ` ${SETTINGS_PAGE_INSET}` : ''
      }${className ? ` ${className}` : ''}`}
    >
      <div className="flex min-w-0 flex-col gap-space-4">
        <div className="flex flex-wrap items-center gap-space-12">
          <Heading id={titleId} className={`${TITLE_TYPOGRAPHY[size]} m-0 text-text-primary`}>
            {title}
          </Heading>
          {pill}
        </div>
        {description && (
          <p className="typography-body-sm m-0 max-w-size-container-lg text-text-secondary">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-space-8">{actions}</div>}
    </div>
  );
};

export default SettingsPageHeader;
